import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHeading from '@/components/base/PageHeading';
import EmptyState from '@/components/base/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { castMembers } from '@/mocks/castMembers';

const PAGE_SIZE = 6;

export default function CastCategory() {
  const [initial, setInitial] = useState('全部');
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta({
    title: '演员名单与角色介绍 - 禁止的爱善良的小姨子电视剧专题',
    description:
      '禁止的爱善良的小姨子演员名单，包含沈知微、陆沉舟、温以宁等八位主创的照片、饰演角色与个人简介，支持按姓名首字母筛选。',
    keywords: '禁止的爱善良的小姨子演员,演员名单,角色介绍,主创阵容',
  });

  const initials = useMemo(() => {
    const set = new Set(castMembers.map((item) => item.initial));
    return ['全部', ...Array.from(set).sort()];
  }, []);

  const filtered = useMemo(() => {
    const list =
      initial === '全部' ? castMembers : castMembers.filter((member) => member.initial === initial);
    return list;
  }, [initial]);

  const shown = filtered.slice(0, visible);

  return (
    <SiteLayout>
      <PageHeading
        crumbs={[{ label: '演员' }]}
        eyebrow="CAST & CHARACTERS"
        title="演员名单与角色"
        description="八位主创，八种处境。这里整理了演员本人资料与剧中角色的位置，帮助你分清谁在守护这个家，谁又在隐瞒什么。"
        count={`${castMembers.length} 位 主创`}
      />

      <section className="bg-background-50">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
          <div className="flex flex-col gap-4 border-b border-background-200 pb-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-2 text-sm text-foreground-500">
              <span className="flex h-4 w-4 items-center justify-center text-base text-primary-500">
                <i className="ri-filter-3-line" />
              </span>
              按姓名首字母筛选
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {initials.map((letter) => (
                <button
                  key={letter}
                  type="button"
                  onClick={() => {
                    setInitial(letter);
                    setVisible(PAGE_SIZE);
                  }}
                  className={`flex h-8 min-w-8 items-center justify-center rounded-md px-2.5 text-xs transition-colors ${
                    initial === letter
                      ? 'bg-primary-600 text-background-50'
                      : 'border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-600'
                  }`}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>

          {shown.length === 0 ? (
            <div className="mt-8">
              <EmptyState
                title="暂无符合条件的演员"
                description="该首字母下暂时没有收录演员资料，可以切换其他字母或查看全部演员。"
                actionLabel="查看全部演员"
                onAction={() => {
                  setInitial('全部');
                  setVisible(PAGE_SIZE);
                }}
              />
            </div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                {shown.map((member, index) => (
                  <Link
                    key={member.id}
                    to={`/detail/cast/${member.id}`}
                    className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 animate-fade-up"
                    style={{ animationDelay: `${Math.min(index, 6) * 40}ms` }}
                  >
                    <div className="relative h-[220px] w-full overflow-hidden bg-background-200 sm:h-[260px]">
                      <img
                        src={member.image}
                        alt={`演员${member.name}饰演${member.role}`}
                        title={`${member.name} - 禁止的爱善良的小姨子演员`}
                        className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground-950/85 to-transparent p-4">
                        <p className="font-heading text-base text-background-50">{member.name}</p>
                        <p className="mt-1 text-xs text-background-100/80">饰 {member.role}</p>
                      </div>
                      <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-background-50/90 text-[11px] text-primary-600">
                        {member.initial}
                      </span>
                    </div>
                    <div className="p-4">
                      <p className="line-clamp-2 text-xs leading-relaxed text-foreground-500">{member.tagline}</p>
                      <span className="mt-3 flex items-center gap-1 text-xs text-accent-700">
                        演员资料
                        <span className="flex h-3.5 w-3.5 items-center justify-center transition-transform group-hover:translate-x-0.5">
                          <i className="ri-arrow-right-line" />
                        </span>
                      </span>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="mt-8 flex flex-col items-center gap-3">
                {visible < filtered.length ? (
                  <button
                    type="button"
                    onClick={() => setVisible((prev) => Math.min(prev + PAGE_SIZE, filtered.length))}
                    className="whitespace-nowrap rounded-md border border-foreground-300 px-6 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-500 hover:text-primary-600"
                  >
                    加载更多演员
                  </button>
                ) : (
                  <p className="text-xs text-foreground-400">已展示全部 {filtered.length} 位演员</p>
                )}
                <p className="text-[11px] text-foreground-400">筛选与分页均为前端演示状态</p>
              </div>
            </>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}